import { Suspense } from "react"
import { useMutation } from "@blitzjs/rpc"
import { useSession } from "@blitzjs/auth"
import { BlitzPage } from "@blitzjs/next"
import { useRouter } from "next/router"
import Loading from "app/core/components/Loading"
import Form from "app/core/components/Form"
import { Button } from "app/core/components/Button"
import { FORM_ERROR } from "final-form"
import { HiArrowRight } from "react-icons/hi"
import { BookingLayout } from "app/core/layouts/BookingLayout"
import { useErrorHandler } from "react-error-boundary"
import { StartNewBookingInput } from "app/bookings/validations"
import { z } from "zod"
import { Tracking } from "app/core/tracking"
import { InitialUserDetails } from "pages/book-testing"
import Spinner from "react-md-spinner"
import style from "app/bookings/bookings.module.css"
import startNewBooking from "app/bookings/mutations/startNewBooking"

function ReadingFluencySignup() {
  const session = useSession({ suspense: false })
  const router = useRouter()
  const [startNewBookingMutation] = useMutation(startNewBooking)
  const handleError = useErrorHandler()

  const schema = session.userId
    ? StartNewBookingInput
    : StartNewBookingInput.extend({
        email: z.string().email(),
      })

  return (
    <div className="bg-gray-100">
      <div className="pt-12 sm:pt-16 lg:pt-20">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl leading-9 font-extrabold text-gray-900 sm:text-4xl sm:leading-10 lg:text-5xl lg:leading-none">
              MAP Reading Fluency
            </h2>
          </div>
        </div>
      </div>
      <div className="mt-8 sm:mt-12 ">
        <div className="relative">
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto rounded-lg shadow-lg overflow-hidden lg:max-w-none lg:grid grid-cols-1 ">
              <div className="py-2 px-3 text-center bg-gray-50 lg:flex lg:flex-col lg:justify-center lg:p-4 h-full col-span-4">
                <Form
                  schema={schema}
                  initialValues={{
                    bookingType: "readingfluency",
                    agreeToTestInUS: true,
                  }}
                  onSubmit={async (values) => {
                    try {
                      if (values.email) {
                        Tracking.setEmail(values.email)
                      }
                      await startNewBookingMutation(values)
                      if (values.email) router.push(`/map-fluency/new/1?guest=${values.email}`)
                      else router.push(`/map-fluency/new/1`)
                    } catch (error) {
                      if (error.name === "AuthenticationError") {
                        handleError(error)
                      }
                      return {
                        [FORM_ERROR]:
                          "Sorry, we had an unexpected error. Please try again. - " +
                          error.toString(),
                      }
                    }
                  }}
                  className="w-full mx-auto"
                >
                  {(form) => (
                    <div className={style["bookings-container"]}>
                      <Suspense fallback={<Loading />}>
                        <InitialUserDetails />
                      </Suspense>

                      <div className="col-span-3 bg-white px-6 py-8 lg:p-12 text-left">
                        <h3 className="text-2xl leading-8 font-extrabold text-gray-900 sm:text-3xl sm:leading-9">
                          MAP Reading Fluency assesses reading ability in grades K-5 in as little as
                          20 minutes.
                        </h3>
                        <p className="mt-4 text-base leading-6 text-gray-500">
                          As an adaptive test, readers and pre-readers will be assessed for
                          different skills.
                        </p>
                        <p className="mt-4 text-base leading-6 font-bold">
                          This assessment is for struggling readers. It will not provide meaningful
                          data for at-grade-level or strong readers.
                        </p>
                        <p className="mt-4 text-base leading-6 text-gray-500">
                          Pre-readers will be assessed in:
                        </p>
                        <ul
                          className="mt-1 text-base leading-6 text-gray-500 px-4"
                          style={{ listStyleType: "circle" }}
                        >
                          <li>Foundational reading skills</li>
                          <ul className="mt-1 px-4" style={{ listStyleType: "disc" }}>
                            <li>
                              Phonological Awareness- Noticing the sounds that make up spoken words,
                              blending sounds to make words, and segmenting words into sounds.
                            </li>
                            <li>
                              Phonics/Word Recognition- Connecting letters to sounds and written
                              words to spoken words.
                            </li>
                            <li>
                              Language Comprehension- Understanding words, sentences and groups of
                              sentences in spoken or written language.
                            </li>
                          </ul>
                        </ul>
                        <p className="mt-4 text-base leading-6 text-gray-500">
                          Readers will be assessed in:
                        </p>
                        <ul
                          className="mt-1 text-base leading-6 text-gray-500 px-4"
                          style={{ listStyleType: "circle" }}
                        >
                          <li>
                            Oral reading fluency: reading aloud that shows an understanding of the
                            passage
                          </li>
                          <li>
                            Literal comprehension: understanding basic facts directly stated in the
                            passage
                          </li>
                        </ul>

                        <p className="mt-6 text-base leading-6 text-gray-500">
                          After you register, you will receive confirmation of your order. Your
                          child’s login credentials will arrive the next business day, and you may
                          use them to test at any time.
                        </p>

                        <p className="mt-6 text-base leading-6 text-gray-500">
                          Requirements: Browser testing
                        </p>
                        <ul
                          className="mt-1 text-base leading-6 text-gray-500 px-4"
                          style={{ listStyleType: "circle" }}
                        >
                          <li>Chrome version 106 or higher</li>
                          <li>Microsoft Edge 106 or higher (Windows only)</li>
                        </ul>
                        <p className="mt-6 text-base leading-6 text-gray-500">Ipad testing</p>
                        <ul
                          className="mt-1 text-base leading-6 text-gray-500 px-4"
                          style={{ listStyleType: "circle" }}
                        >
                          <li>iPadOS 14 or higher</li>
                          <li>App Version 7.1</li>
                          <li>
                            <a
                              href="https://apps.apple.com/us/app/map-reading-fluency-testing/id1326709796"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              MAP Reading Fluency Testing
                            </a>
                          </li>
                        </ul>
                      </div>

                      <Button disabled={form.invalid} className="px-5">
                        {form.submitting ? (
                          <Spinner size={26} singleColor="#ffffff" />
                        ) : (
                          <>
                            Next <HiArrowRight className="mt-px ml-3" />
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const MAPFluencyPage: BlitzPage = () => {
  return (
    <Suspense fallback={<div />}>
      <ReadingFluencySignup />
    </Suspense>
  )
}

MAPFluencyPage.getLayout = (page) => (
  <BookingLayout title="MAP Reading Fluency" subtitle="NWEA MAP Reading Fluency Test Online">
    {page}
  </BookingLayout>
)

export default MAPFluencyPage
